// Core IndexedDB operations and database setup

const DB_NAME = 'MouldFlowSurveys';
const DB_VERSION = 5;

export const STORES = {
    CACHED_SURVEYS: 'cachedSurveys',
    LOCAL_REPORTS: 'localReports',
    PHOTO_CACHE: 'photoCache',
    PHOTO_OUTBOX: 'photoOutbox',
    SYNC_EVENTS: 'syncEvents',
    CACHED_USER: 'cachedUser',
    SW_ERRORS: 'swErrors'
} as const;

/**
 * Service worker error record stored in IndexedDB.
 * Used to persist errors that occur when no client pages are open.
 */
export interface SwErrorRecord {
    /** Unique identifier for the error */
    id: string;
    /** ISO timestamp when the error occurred */
    timestamp: string;
    /** Type of error */
    type: 'exception' | 'rejection' | 'fetch' | 'sync';
    /** Error message */
    message: string;
    /** Error stack trace */
    stack?: string;
    /** URL where the error occurred */
    url?: string;
    /** Service worker version when error occurred */
    swVersion: string;
}

export type StoreName = typeof STORES[keyof typeof STORES];

let cachedDb: IDBDatabase | null = null;

function openDatabaseInternal(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('[IndexedDB] Failed to open database:', request.error);
            reject(request.error);
        };

        request.onsuccess = () => {
            console.log('[IndexedDB] Database opened successfully');
            cachedDb = request.result;
            resolve(cachedDb);
        };

        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
            console.log('[IndexedDB] Upgrading database...');
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(STORES.CACHED_SURVEYS)) {
                const surveyStore = db.createObjectStore(STORES.CACHED_SURVEYS, { keyPath: 'id' });
                surveyStore.createIndex('dueDate', 'dueDate', { unique: false });
                console.log('[IndexedDB] Created cachedSurveys store');
            }

            if (!db.objectStoreNames.contains(STORES.LOCAL_REPORTS)) {
                const reportStore = db.createObjectStore(STORES.LOCAL_REPORTS, { keyPath: 'surveyId' });
                reportStore.createIndex('isComplete', 'isComplete', { unique: false });
                reportStore.createIndex('isSynced', 'isSynced', { unique: false });
                console.log('[IndexedDB] Created localReports store');
            }

            if (!db.objectStoreNames.contains(STORES.PHOTO_CACHE)) {
                const photoStore = db.createObjectStore(STORES.PHOTO_CACHE, { keyPath: 'blobName' });
                photoStore.createIndex('surveyId', 'surveyId', { unique: false });
                photoStore.createIndex('isUploaded', 'isUploaded', { unique: false });
                console.log('[IndexedDB] Created photoCache store');
            }

            if (!db.objectStoreNames.contains(STORES.PHOTO_OUTBOX)) {
                const outboxStore = db.createObjectStore(STORES.PHOTO_OUTBOX, { keyPath: 'blobName' });
                outboxStore.createIndex('status', 'status', { unique: false });
                outboxStore.createIndex('surveyId', 'surveyId', { unique: false });
                console.log('[IndexedDB] Created photoOutbox store');
            }

            if (!db.objectStoreNames.contains(STORES.SYNC_EVENTS)) {
                const syncStore = db.createObjectStore(STORES.SYNC_EVENTS, { keyPath: 'id' });
                syncStore.createIndex('startedAt', 'startedAt', { unique: false });
                console.log('[IndexedDB] Created syncEvents store');
            }

            if (!db.objectStoreNames.contains(STORES.CACHED_USER)) {
                db.createObjectStore(STORES.CACHED_USER, { keyPath: 'key' });
                console.log('[IndexedDB] Created cachedUser store');
            }

            if (!db.objectStoreNames.contains(STORES.SW_ERRORS)) {
                const swErrorStore = db.createObjectStore(STORES.SW_ERRORS, { keyPath: 'id' });
                swErrorStore.createIndex('timestamp', 'timestamp', { unique: false });
                console.log('[IndexedDB] Created swErrors store');
            }
        };
    });
}

export async function openDatabase(): Promise<IDBDatabase> {
    if (cachedDb) {
        return cachedDb;
    }
    return await openDatabaseInternal();
}

function wrapRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

export async function getAllFromStore<T>(storeName: StoreName): Promise<T[]> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const result = await wrapRequest(store.getAll());
    return (result as T[]) || [];
}

export async function getFromStore<T>(storeName: StoreName, key: string): Promise<T | null> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const result = await wrapRequest(store.get(key));
    return (result as T) || null;
}

export async function putToStore<T>(storeName: StoreName, item: T): Promise<IDBValidKey> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    return wrapRequest(store.put(item));
}

export async function deleteFromStore(storeName: StoreName, key: string): Promise<void> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    await wrapRequest(store.delete(key));
}

export async function clearStore(storeName: StoreName): Promise<void> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    await wrapRequest(store.clear());
}

export async function getByIndex<T>(storeName: StoreName, indexName: string, value: IDBValidKey): Promise<T[]> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const result = await wrapRequest(index.getAll(value));
    return (result as T[]) || [];
}

export async function countStore(storeName: StoreName): Promise<number> {
    const db = await openDatabase();
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    return wrapRequest(store.count());
}

export async function clearAllStores(): Promise<void> {
    const db = await openDatabase();
    const storeNames = Object.values(STORES);
    
    for (const storeName of storeNames) {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        await wrapRequest(store.clear());
    }
    
    console.log('[IndexedDB] All stores cleared');
}

// ============================================================================
// Service Worker Error Functions
// Used to persist SW errors when no client pages are open
// ============================================================================

/**
 * Stores a service worker error in IndexedDB.
 * Called from the service worker when an error occurs.
 * 
 * @param error - The error record to store
 */
export async function storeSwError(error: SwErrorRecord): Promise<void> {
    try {
        await putToStore(STORES.SW_ERRORS, error);
        console.log('[IndexedDB] Stored SW error:', error.id);
    } catch (err) {
        console.error('[IndexedDB] Failed to store SW error:', err);
    }
}

/**
 * Retrieves all stored service worker errors.
 * Called from the main thread on app startup.
 * 
 * @returns Array of stored SW error records
 */
export async function getSwErrors(): Promise<SwErrorRecord[]> {
    try {
        return await getAllFromStore<SwErrorRecord>(STORES.SW_ERRORS);
    } catch (err) {
        console.error('[IndexedDB] Failed to get SW errors:', err);
        return [];
    }
}

/**
 * Clears all stored service worker errors.
 * Called after errors have been forwarded to Application Insights.
 */
export async function clearSwErrors(): Promise<void> {
    try {
        await clearStore(STORES.SW_ERRORS);
        console.log('[IndexedDB] Cleared SW errors');
    } catch (err) {
        console.error('[IndexedDB] Failed to clear SW errors:', err);
    }
}

// Initialize database on module load
openDatabase().catch(err => console.error('[IndexedDB] Auto-init failed:', err));
